import { useState, useEffect } from 'react';
import { Modal } from '../Modal';
import { ClientModal } from '../ClientModal';
import { staffService, servicesService, clientsService, appointmentsService } from '../../services/api';
import { brisbaneInputsToUtc, utcToBrisbaneInputs } from '../../utils/dateFormatters';
import './AppointmentModal.css';

const STATUSES = ['pending', 'confirmed', 'completed', 'no_show', 'cancelled'];

export default function AppointmentModal({ isOpen, onClose, onSave, appointment = null, defaultStaffId = null, defaultDate = '' }) {
  const isEdit = !!appointment;
  const [staff, setStaff] = useState([]);
  const [services, setServices] = useState([]);
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState({
    staff_id: '', service_id: '', customer_id: '', date: '', time: '', status: 'confirmed', notes: '', admin_notes: ''
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [showClientModal, setShowClientModal] = useState(false);

  // Load reference data + prefill on open
  useEffect(() => {
    if (!isOpen) return;
    setErrors({});
    (async () => {
      const [s, c] = await Promise.all([staffService.getAll(), clientsService.getAll()]);
      if (s.success) setStaff((s.staff || []).filter((p) => p.is_bookable && p.is_active));
      if (c.success) setClients(c.customers || []);
    })();

    if (appointment) {
      const { date, time } = utcToBrisbaneInputs(appointment.starts_at);
      setForm({
        staff_id: String(appointment.staff_id || ''),
        service_id: String(appointment.service_id || ''),
        customer_id: String(appointment.customer_id || ''),
        date, time,
        status: appointment.status || 'confirmed',
        notes: appointment.notes || '',
        admin_notes: appointment.admin_notes || ''
      });
    } else {
      setForm({
        staff_id: defaultStaffId ? String(defaultStaffId) : '',
        service_id: '', customer_id: '', date: defaultDate || '', time: '', status: 'confirmed', notes: '', admin_notes: ''
      });
    }
  }, [isOpen, appointment, defaultStaffId, defaultDate]);

  // Load the selected person's services when staff changes
  useEffect(() => {
    if (!isOpen || !form.staff_id) { setServices([]); return; }
    servicesService.getAll({ staffId: form.staff_id }).then((r) => {
      if (r.success) setServices((r.services || []).filter((s) => s.is_active));
    });
  }, [isOpen, form.staff_id]);

  function change(e) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  }

  function onNewClient(client) {
    setClients((prev) => [...prev, client]);
    setForm((prev) => ({ ...prev, customer_id: String(client.id) }));
    setShowClientModal(false);
  }

  function validate() {
    const e = {};
    if (!form.staff_id) e.staff_id = 'Choose a person';
    if (!form.service_id) e.service_id = 'Choose a meeting type';
    if (!form.date) e.date = 'Pick a date';
    if (!form.time) e.time = 'Pick a time';
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function submit(e) {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    const startsAt = brisbaneInputsToUtc(form.date, form.time);
    try {
      let response;
      if (isEdit) {
        // Move time if it changed, then update the other fields.
        if (startsAt !== appointment.starts_at) {
          const r = await appointmentsService.reschedule(appointment.id, startsAt);
          if (!r.success) { setErrors({ submit: r.error || 'Could not reschedule' }); setSaving(false); return; }
        }
        response = await appointmentsService.update(appointment.id, {
          customer_id: form.customer_id ? Number(form.customer_id) : null,
          status: form.status,
          notes: form.notes,
          admin_notes: form.admin_notes
        });
      } else {
        response = await appointmentsService.create({
          serviceId: Number(form.service_id),
          customerId: form.customer_id ? Number(form.customer_id) : null,
          startsAt,
          status: form.status,
          notes: form.notes,
          adminNotes: form.admin_notes
        });
      }
      if (response.success) {
        onSave?.(response.appointment);
        onClose();
      } else {
        setErrors({ submit: response.error || 'Failed to save appointment' });
      }
    } catch (err) {
      setErrors({ submit: 'An error occurred. Please try again.' });
    } finally {
      setSaving(false);
    }
  }

  const selectedService = services.find((s) => String(s.id) === form.service_id);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={isEdit ? 'Edit Appointment' : 'New Appointment'} size="large">
      <form onSubmit={submit} className="appt-form">
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="staff_id">Person *</label>
            <select id="staff_id" name="staff_id" value={form.staff_id} onChange={change} disabled={isEdit}>
              <option value="">Select…</option>
              {staff.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            {errors.staff_id && <span className="form-error">{errors.staff_id}</span>}
          </div>
          <div className="form-group">
            <label htmlFor="service_id">Meeting type *</label>
            <select id="service_id" name="service_id" value={form.service_id} onChange={change} disabled={!form.staff_id}>
              <option value="">Select…</option>
              {services.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.duration_minutes} min)</option>)}
            </select>
            {errors.service_id && <span className="form-error">{errors.service_id}</span>}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="customer_id">Client</label>
          <div className="inline-select-row">
            <select id="customer_id" name="customer_id" value={form.customer_id} onChange={change}>
              <option value="">No client / walk-in</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}{c.email ? ` — ${c.email}` : ''}</option>)}
            </select>
            <button type="button" className="btn btn-outline" onClick={() => setShowClientModal(true)}>+ New</button>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="date">Date *</label>
            <input id="date" name="date" type="date" value={form.date} onChange={change} />
            {errors.date && <span className="form-error">{errors.date}</span>}
          </div>
          <div className="form-group">
            <label htmlFor="time">Time *</label>
            <input id="time" name="time" type="time" value={form.time} onChange={change} />
            {errors.time && <span className="form-error">{errors.time}</span>}
          </div>
          <div className="form-group">
            <label htmlFor="status">Status</label>
            <select id="status" name="status" value={form.status} onChange={change}>
              {STATUSES.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
            </select>
          </div>
        </div>

        {selectedService && (
          <p className="appt-hint text-muted">
            {selectedService.duration_minutes} min · {selectedService.location_mode.replace('_', ' ')}
            {selectedService.price ? ` · $${selectedService.price}` : ''}
          </p>
        )}

        <div className="form-group">
          <label htmlFor="notes">Notes (visible to client)</label>
          <textarea id="notes" name="notes" rows="2" value={form.notes} onChange={change} />
        </div>
        <div className="form-group">
          <label htmlFor="admin_notes">Private notes</label>
          <textarea id="admin_notes" name="admin_notes" rows="2" value={form.admin_notes} onChange={change} />
        </div>

        {errors.submit && <div className="form-error text-center mb-md">{errors.submit}</div>}

        <div className="modal-actions">
          <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving…' : (isEdit ? 'Save Changes' : 'Create Appointment')}
          </button>
        </div>
      </form>

      <ClientModal isOpen={showClientModal} onClose={() => setShowClientModal(false)} onSave={onNewClient} />
    </Modal>
  );
}
